import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { supplierService } from '../../services/supplierService';

const RoleBasedNavigation = ({ notificationCount = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut, isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  // Derive user role from userProfile
  const userRole = userProfile?.role || null;

  const customerMenuItems = [
    { label: 'Dashboard', path: '/customer-dashboard', icon: 'LayoutDashboard' },
    { label: 'Browse Kitchens', path: '/browse-kitchens', icon: 'ChefHat' },
    { label: 'My Orders', path: '/my-orders', icon: 'ShoppingBag' },
    { label: 'Messages', path: '/real-time-messaging', icon: 'MessageSquare' },
    { label: 'Shopping Cart', path: '/shopping-cart', icon: 'ShoppingCart' },
  ];

  const supplierMenuItems = [
    { label: 'Dashboard', path: '/supplier-dashboard', icon: 'LayoutDashboard' },
    { label: 'My Kitchen', path: '/supplier-profile', icon: 'Store' },
    { label: 'Orders', path: '/order-placement', icon: 'ClipboardList' },
    { label: 'Messages', path: '/real-time-messaging', icon: 'MessageSquare' },
  ];

  const adminMenuItems = [
    { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Shield' },
    { label: 'Suppliers', path: '/admin-dashboard', icon: 'Users' },
    { label: 'Settings', path: '/admin-dashboard', icon: 'Settings' },
  ];

  const unauthenticatedMenuItems = [
    { label: 'Browse Kitchens', path: '/browse-kitchens', icon: 'ChefHat' },
  ];

  const getMenuItems = () => {
    if (!isAuthenticated) return unauthenticatedMenuItems;
    if (userRole === 'customer') return customerMenuItems;
    if (userRole === 'supplier') return supplierMenuItems;
    if (userRole === 'admin') return adminMenuItems;
    return [];
  };

  const menuItems = getMenuItems();

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleNavigation = async (path) => {
    setIsMobileMenuOpen(false);

    // Special-case: resolve supplier ID for "My Kitchen" and redirect appropriately
    if (path === '/supplier-profile' && isAuthenticated && userRole === 'supplier') {
      try {
        const { data: supplier, error } = await supplierService?.getSupplierByUserId(user?.id);
        if (!error && supplier?.id) {
          navigate(`/supplier-profile/${supplier.id}`);
          return;
        }
        // No supplier record exists yet - send user to supplier dashboard to complete setup
        navigate('/supplier-dashboard');
        return;
      } catch (err) {
        console.error('Error resolving supplier profile:', err);
        navigate('/supplier-dashboard');
        return;
      }
    }

    navigate(path);
  }; 

  const handleAuthAction = (action) => {
    navigate(action === 'login' ? '/login' : '/register');
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 bg-card shadow-warm z-navigation">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-8">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 transition-smooth hover:opacity-80"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="ChefHat" size={28} color="var(--color-primary)" />
                </div>
                <span className="text-2xl font-heading font-bold text-foreground">
                  HungryElph
                </span>
              </button>

              <div className="hidden lg:flex items-center gap-2">
                {menuItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-smooth ${
                      isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-medium">{item?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-4">
              {isAuthenticated && userRole === 'customer' && (
                <div className="relative">
                  <button
                    onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                    className="p-2 rounded-lg hover:bg-muted transition-smooth"
                  >
                    <Icon name="Search" size={20} />
                  </button>
                  {isSearchExpanded && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-popover rounded-lg shadow-warm-lg p-4">
                      <input
                        type="text"
                        placeholder="Search for cuisines, dishes..."
                        className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              )}

              {isAuthenticated && (
                <button className="relative p-2 rounded-lg hover:bg-muted transition-smooth">
                  <Icon name="Bell" size={20} />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs font-bold rounded-full flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
              )}

              {isAuthenticated ? (
                <div className="relative">
                  <button 
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="p-2 rounded-lg hover:bg-muted transition-smooth"
                  >
                    <Icon name="User" size={20} />
                  </button>
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-popover rounded-lg shadow-warm-lg py-2 border border-border">
                      <div className="px-4 py-2 border-b border-border">
                        <p className="text-sm font-semibold truncate">
                          {userProfile?.full_name || user?.email}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {user?.email}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          if (userRole === 'customer') {
                            navigate('/customer-profile-edit');
                          } else if (userRole === 'supplier') {
                            handleNavigation('/supplier-profile');
                          }
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-muted transition-smooth flex items-center gap-2"
                      >
                        <Icon name="User" size={16} />
                        <span>Profile</span>
                      </button>
                      <button
                        onClick={async () => {
                          setIsProfileMenuOpen(false);
                          await signOut();
                          navigate('/login');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-muted transition-smooth flex items-center gap-2 text-destructive"
                      >
                        <Icon name="LogOut" size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Button variant="ghost" onClick={() => handleAuthAction('login')}>
                    Sign In
                  </Button>
                  <Button variant="default" onClick={() => handleAuthAction('register')}>
                    Get Started
                  </Button>
                </div>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-smooth"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </nav>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-background z-dropdown lg:hidden" style={{ top: '80px' }}>
          <div className="flex flex-col h-full p-6 overflow-y-auto">
            <div className="flex flex-col gap-2 mb-6">
              {menuItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={24} />
                  <span className="font-medium text-lg">{item?.label}</span>
                </button>
              ))}
            </div>

            {isAuthenticated && userRole === 'customer' && (
              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Search for cuisines, dishes..."
                  className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            )}

            {isAuthenticated ? (
              <div className="flex flex-col gap-3 mt-auto">
                <button className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-smooth">
                  <Icon name="Bell" size={24} />
                  <span className="font-medium text-lg">Notifications</span>
                  {notificationCount > 0 && (
                    <span className="ml-auto w-6 h-6 bg-accent text-accent-foreground text-sm font-bold rounded-full flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
                <button 
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    if (userRole === 'customer') {
                      navigate('/customer-profile-edit');
                    } else if (userRole === 'supplier') {
                      handleNavigation('/supplier-profile');
                    }
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-smooth"
                >
                  <Icon name="User" size={24} />
                  <span className="font-medium text-lg">Profile</span>
                </button>
                <button
                  onClick={async () => {
                    setIsMobileMenuOpen(false);
                    await signOut();
                    navigate('/login');
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-smooth text-destructive"
                >
                  <Icon name="LogOut" size={24} />
                  <span className="font-medium text-lg">Logout</span>
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3 mt-auto">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => handleAuthAction('login')}
                >
                  Sign In
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => handleAuthAction('register')}
                >
                  Get Started
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      <div style={{ height: '80px' }} />
    </>
  );
};

export default RoleBasedNavigation;